import { FC, useCallback, useEffect } from "react";
import { Row, Container, Button, Form, FloatingLabel } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import PasswordRules from "../password-rules/PasswordRules";
import invitationPageSlice from "./store/slice";
import styles from "./invitationPage.module.scss";
import LogoIcon from "../../images/logo-icon.svg";

export type InvitationFormValues = {
  email: string;
  password: string;
  password_confirmation: string;
  token: string;
};

const InvitationPage: FC = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();

  const email = searchParams.get("email");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isLoading = useSelector((state) => state.invitationPage.isLoading);
  const invited = useSelector((state) => state.invitationPage.invited);

  const acceptInvitation = useCallback(
    (data: InvitationFormValues) =>
      dispatch(invitationPageSlice.actions.accept(data)),
    [dispatch]
  );
  const reset = useCallback(
    () => dispatch(invitationPageSlice.actions.reset()),
    [dispatch]
  );

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<InvitationFormValues>({
    defaultValues: {
      email: email || "",
    },
  });

  useEffect(() => {
    // Reset if user was logged in (when testing)
    localStorage.removeItem("jwt_tokens");

    return () => {
      reset();
    };
  }, [reset]);

  useEffect(() => {
    if (invited) {
      navigate("/");
    }
  }, [navigate, invited]);

  const onSubmit = async (data: InvitationFormValues) => {
    if (token) {
      acceptInvitation({
        ...data,
        token,
      });
    }
  };

  return (
    <Container fluid className={styles.container}>
      <Row className={styles.row}>
        <Form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
          <img alt="logo" width={200} src={LogoIcon} className={styles.logo} />

          <h1 className={styles.title}>Registration</h1>

          <FloatingLabel
            controlId="email"
            label="Email address"
            className="mb-3"
          >
            <Form.Control
              {...register("email", {
                required: true,
              })}
              type="email"
              placeholder="Email address"
              disabled
            />
          </FloatingLabel>

          <FloatingLabel controlId="password" label="Password" className="mb-3">
            <Form.Control
              type="password"
              placeholder="Password"
              isInvalid={!!errors.password}
              {...register("password", {
                required: true,
                minLength: 8,
                validate: {
                  containsUpper: (value) => /[A-Z]/.test(value),
                  containsLower: (value) => /[a-z]/.test(value),
                  containsDigits: (value) => /[0-9]/.test(value),
                },
              })}
              maxLength={100}
              disabled={isLoading}
            />
            <Form.Control.Feedback type="invalid">
              {errors.password?.type === "required" && "Required field."}
              {errors.password?.type === "minLength" &&
                "Minimum 8 characteres required."}
              {errors.password?.type === "containsUpper" &&
                "At least 1 upper case letter required."}
              {errors.password?.type === "containsLower" &&
                "At least 1 lower case letter required."}
              {errors.password?.type === "containsDigits" &&
                "At least 1 number required."}
            </Form.Control.Feedback>
          </FloatingLabel>

          <FloatingLabel
            controlId="password_confirmation"
            label="Confirm password"
            className="mb-3"
          >
            <Form.Control
              type="password"
              placeholder="Confirm password"
              isInvalid={!!errors.password}
              {...register("password_confirmation", {
                required: true,
                minLength: 8,
                validate: (value) => value === watch("password"),
              })}
              maxLength={100}
              disabled={isLoading}
            />
            <Form.Control.Feedback type="invalid">
              {errors.password_confirmation?.type === "required" &&
                "Required field."}
              {errors.password_confirmation?.type === "validate" &&
                "Passwords don't match."}
              {errors.password_confirmation?.type === "minLength" &&
                "Minimum 8 characteres required."}
            </Form.Control.Feedback>
          </FloatingLabel>

          <PasswordRules />

          <div className={styles.createButton}>
            <Button variant="primary" type="submit" disabled={isLoading}>
              Create profile
            </Button>
          </div>
        </Form>
      </Row>
    </Container>
  );
};

export default InvitationPage;
